
import { useState, useEffect, useMemo } from 'react';
import { InsightCard } from '@/types/insight';

interface UseInsightsPaginationProps {
  cards: InsightCard[];
  initialItemsPerPage?: number;
  initialPage?: number;
}

type SortDirection = 'asc' | 'desc';

export const useInsightsPagination = ({
  cards,
  initialItemsPerPage = 10,
  initialPage = 1
}: UseInsightsPaginationProps) => {
  // Pagination state
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [itemsPerPage, setItemsPerPage] = useState(initialItemsPerPage);
  
  // Filtering state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [countryFilter, setCountryFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  
  // Sorting state
  const [sortField, setSortField] = useState<keyof InsightCard>('created_at');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, countryFilter, categoryFilter]);

  // Apply filters and sorting
  const filteredCards = useMemo(() => {
    let result = [...cards];
    
    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      result = result.filter(card => 
        card.title.toLowerCase().includes(searchLower) || 
        card.description.toLowerCase().includes(searchLower)
      );
    }
    
    // Apply status filter
    if (statusFilter !== 'all') {
      result = result.filter(card => card.status === statusFilter);
    }
    
    // Apply country filter
    if (countryFilter !== 'all') {
      result = result.filter(card => 
        countryFilter === 'global' ? 
          card.country_code === null : 
          card.country_code === countryFilter
      );
    }
    
    // Apply category filter
    if (categoryFilter !== 'all') {
      result = result.filter(card => card.category_id === categoryFilter);
    }
    
    // Sort the filtered results
    result.sort((a, b) => {
      const fieldA = a[sortField];
      const fieldB = b[sortField];
      
      // Handle different field types
      if (typeof fieldA === 'string' && typeof fieldB === 'string') {
        return sortDirection === 'asc' 
          ? fieldA.localeCompare(fieldB) 
          : fieldB.localeCompare(fieldA);
      }
      
      if (fieldA === fieldB) return 0;
      
      // Handle null/undefined
      if (fieldA == null) return sortDirection === 'asc' ? -1 : 1;
      if (fieldB == null) return sortDirection === 'asc' ? 1 : -1;
      
      // Default comparison
      return sortDirection === 'asc'
        ? fieldA < fieldB ? -1 : 1
        : fieldA > fieldB ? -1 : 1;
    });
    
    return result;
  }, [cards, searchTerm, statusFilter, countryFilter, categoryFilter, sortField, sortDirection]);
  
  // Calculate pagination values
  const totalItems = filteredCards.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  
  // Ensure current page is valid
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);
  
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalItems);
  const paginatedCards = filteredCards.slice(startIndex, endIndex);
  
  // Handle sort toggling
  const handleSort = (field: keyof InsightCard) => {
    if (sortField === field) {
      // Toggle direction if clicking the same field
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      // Default to descending for new sort field
      setSortField(field);
      setSortDirection('desc');
    }
  };
  
  return {
    // Pagination
    currentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage,
    totalPages,
    totalItems,
    startIndex,
    endIndex,
    paginatedCards,
    
    // Filtering
    searchTerm,
    setSearchTerm,
    statusFilter,
    setStatusFilter,
    countryFilter,
    setCountryFilter,
    categoryFilter,
    setCategoryFilter,
    
    // Sorting
    sortField,
    sortDirection,
    handleSort,
    
    // Helpers
    resetFilters: () => {
      setSearchTerm('');
      setStatusFilter('all');
      setCountryFilter('all');
      setCategoryFilter('all');
    }
  };
};
